<template>
    <v-row>
        <v-col cols="12" md="8">
            <h3 class='mb-3'>{{ $t('profitLosses.title') }}</h3>
            <p>{{ $t('profitLosses.subtitle') }}</p>
            <apexchart :options="optionsStrategiesProfit" :series="seriesStrategiesProfit" height="320" type="bar">
            </apexchart>
        </v-col>

        <v-col class="d-flex align-center justify-center" cols="2">
            <div>
                <h2>{{ mostProfitableStrategy }}</h2>
                <p>{{ $t('indicators.mostProfitable') }}</p>

                <h2>{{ lessProfitableStrategy }}</h2>
                <p>{{ $t('indicators.leastProfitable') }}</p>
            </div>
        </v-col>
        <v-col class="d-flex align-center justify-center" cols="2">
            <div>
                <h2>{{ mostUsedStrategy }}</h2>
                <p>{{ $t('indicators.mostUsed') }}</p>

                <h2>{{ lessUsedStrategy }}</h2>
                <p>{{ $t('indicators.fewestUsed') }}</p>
            </div>
        </v-col>
    </v-row>
</template>

<script>
export default {
    name: "StrategyStats",
    data() {
        return {
            // Data
            mostProfitableStrategy: '',
            lessProfitableStrategy: '',
            mostUsedStrategy: '',
            lessUsedStrategy: '',

            // Options
            optionsStrategiesProfit: {
                theme: {
                    mode: this.$store.state.currentTheme == "dark" ? "dark" : "light"
                },
                noData: {
                    text: 'No data for the current filter',
                    align: 'center',
                    verticalAlign: 'middle',
                    style: {
                        color: '#111111',
                        fontSize: '21px',
                        fontFamily: 'Ubuntu, sans-serif',
                    }
                },
                xaxis: {
                    categories: [],
                    title: {
                        text: 'Monetary performance per strategy'
                    },
                    labels: {
                        formatter: function (val) {
                            return val
                        },
                        style: {
                            color: '#6C7079',
                            fontSize: '12px'
                        }
                    }
                },
                chart: {
                    type: 'bar',
                    height: 320,
                    stacked: true
                },
                colors: ['rgb(36,120,81)', '#FF4747'],
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '80%',
                    },
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    width: 1,
                    colors: ["#fff"]
                },
                grid: {
                    xaxis: {
                        lines: {
                            show: false
                        }
                    }
                },
                yaxis: {
                    labels: {
                        style: {
                            color: '#6C7079',
                            fontSize: '12px'
                        },
                    }
                },
                tooltip: {
                    shared: false,
                    x: {
                        formatter: function (val) {
                            return val
                        }
                    },
                    y: {
                        formatter: function (val) {
                            return val
                        }
                    }
                },
            },

            // Series
            seriesStrategiesProfit: [],
        };
    },
    mounted() {
        this.setStats();
    },
    watch: {
        '$store.state.book.trades': function () {
            this.setStats();
        }
    },
    methods: {
        setStats() {
            let mapStrategies = {};
            let newLabels = [];
            let profitValues = [];
            let lossesValues = [];
            let copyOfTrades = this.$store.state.book.trades.slice();

            // Other stats
            let mostProfitable = Number.MIN_SAFE_INTEGER;
            let lessProfitable = Number.MAX_SAFE_INTEGER;
            let mostUsed = Number.MIN_SAFE_INTEGER;
            let lessUsed = Number.MAX_SAFE_INTEGER;

            copyOfTrades.forEach((trade) => {
                // Create a new strategy
                if (trade.strategy != null) {
                    if (!(trade.strategy.name in mapStrategies))
                        if (trade.netProfit > 0)
                            mapStrategies[trade.strategy.name] = {
                                profits: trade.netProfit,
                                losses: 0,
                                count: 1,
                            };
                        else
                            mapStrategies[trade.strategy.name] = {
                                profits: 0,
                                losses: trade.netProfit,
                                count: 1,
                            };

                    // Set values for existing strategy
                    else if (trade.netProfit > 0)
                        mapStrategies[trade.strategy.name] = {
                            profits: (mapStrategies[trade.strategy.name].profits) + trade.netProfit,
                            losses: (mapStrategies[trade.strategy.name].losses),
                            count: (mapStrategies[trade.strategy.name].count) + 1,
                        };
                    else
                        mapStrategies[trade.strategy.name] = {
                            profits: (mapStrategies[trade.strategy.name].profits),
                            losses: (mapStrategies[trade.strategy.name].losses) + trade.netProfit,
                            count: (mapStrategies[trade.strategy.name].count) + 1,
                        };
                }
            });

            for (let strategy in mapStrategies) {
                let profit = mapStrategies[strategy].profits;
                let losses = mapStrategies[strategy].losses;
                let count = mapStrategies[strategy].count;
                newLabels.push(strategy);
                profitValues.push(profit);
                lossesValues.push(losses);

                mostProfitable = Math.max(mostProfitable, (profit + losses));
                if (mostProfitable === (profit + losses))
                    this.mostProfitableStrategy = strategy;

                lessProfitable = Math.min(lessProfitable, (profit + losses));
                if (lessProfitable === (profit + losses))
                    this.lessProfitableStrategy = strategy;

                mostUsed = Math.max(mostUsed, count);
                if (mostUsed === count)
                    this.mostUsedStrategy = strategy;

                lessUsed = Math.min(lessUsed, count);
                if (lessUsed === count)
                    this.lessUsedStrategy = strategy;
            }

            this.seriesStrategiesProfit = [
                {
                    name: 'Profit',
                    data: profitValues,
                },
                {
                    name: 'Losses',
                    data: lossesValues,
                }
            ];
            this.optionsStrategiesProfit = {
                theme: {
                    mode: this.$store.state.currentTheme == "dark" ? "dark" : "light"
                },
                xaxis: {
                    categories: newLabels,
                    title: {
                        text: 'Monetary performance per strategy'
                    },
                    labels: {
                        formatter: function (val) {
                            return val.toFixed(2);
                        },
                    }
                },
                chart: {
                    type: 'bar',
                    height: 320,
                    stacked: true
                },
                colors: ['rgb(36,120,81)', '#FF4747'],
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '80%',
                    },
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    width: 1,
                    colors: ["#fff"]
                },
                grid: {
                    xaxis: {
                        lines: {
                            show: false
                        }
                    }
                },
                yaxis: {
                    labels: {
                        style: {
                            color: '#6C7079',
                            fontSize: '12px'
                        },

                    },
                },
                tooltip: {
                    shared: false,
                    x: {
                        formatter: function (val) {
                            return val
                        }
                    },
                    y: {
                        formatter: function (val) {
                            return val.toFixed(2);
                        }
                    }
                },
            };
        },
    }
}
</script>



<style scoped></style>
